import { useEffect, useState } from "react";
import { Button } from "@app/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@app/components/ui/dialog";
import { CheckCircle2, Edit2 } from "lucide-react";
import type { ExperimentRun } from "@app/components/RunsTable";

interface ValidationDialogProps {
  open: boolean;
  onClose: () => void;
  run: ExperimentRun | null;
  onSubmit: (
    corrections: Record<string, unknown>,
    validatedFields: string[],
  ) => Promise<void>;
}

export function ValidationDialog({
  open,
  onClose,
  run,
  onSubmit,
}: ValidationDialogProps) {
  const fields = Object.entries(run?.actual_output || {});

  const [statuses, setStatuses] = useState<Record<string, "ok" | "corrected">>(
    () => Object.fromEntries(fields.map(([k]) => [k, "ok" as const])),
  );
  const [corrections, setCorrections] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      fields.map(([k, v]) => [
        k,
        typeof v === "string" ? v : JSON.stringify(v),
      ]),
    ),
  );
  const [submitting, setSubmitting] = useState(false);

  // Reset state when run changes
  const fieldKeys = fields.map(([k]) => k).join(",");
  useEffect(() => {
    setStatuses(Object.fromEntries(fields.map(([k]) => [k, "ok" as const])));
    setCorrections(
      Object.fromEntries(
        fields.map(([k, v]) => [
          k,
          typeof v === "string" ? v : JSON.stringify(v),
        ]),
      ),
    );
  // fieldKeys is a stable primitive; using fields would cause infinite loop (new ref each render)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldKeys]);

  const handleSubmit = async () => {
    setSubmitting(true);
    const correctedFields: Record<string, unknown> = {};
    const validatedFields: string[] = [];
    for (const [key, st] of Object.entries(statuses)) {
      if (st === "corrected") {
        try {
          correctedFields[key] = JSON.parse(corrections[key]);
        } catch {
          correctedFields[key] = corrections[key];
        }
      } else {
        validatedFields.push(key);
      }
    }
    await onSubmit(correctedFields, validatedFields);
    setSubmitting(false);
    onClose();
  };

  const correctedCount = Object.values(statuses).filter(
    (s) => s === "corrected",
  ).length;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[620px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Valider l'extraction IA</DialogTitle>
        </DialogHeader>

        {fields.length === 0 ? (
          <p className="text-slate-500 text-sm py-4">
            Aucune extraction disponible pour ce run.
          </p>
        ) : (
          <div className="space-y-3 py-2">
            <p className="text-xs text-slate-500">
              Validez chaque champ extrait. Les corrections seront enregistrées
              comme exemples d'apprentissage.
            </p>
            {fields.map(([key, value]) => (
              <div
                key={`${fieldKeys}-${key}`}
                className="border border-slate-200 rounded-lg p-3"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm text-slate-700 font-mono">
                    {key}
                  </span>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant={statuses[key] === "ok" ? "default" : "outline"}
                      className="h-7 text-xs"
                      onClick={() =>
                        setStatuses((s) => ({ ...s, [key]: "ok" }))
                      }
                    >
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Correct
                    </Button>
                    <Button
                      size="sm"
                      variant={
                        statuses[key] === "corrected" ? "destructive" : "outline"
                      }
                      className="h-7 text-xs"
                      onClick={() =>
                        setStatuses((s) => ({ ...s, [key]: "corrected" }))
                      }
                    >
                      <Edit2 className="h-3 w-3 mr-1" />
                      Corriger
                    </Button>
                  </div>
                </div>
                {statuses[key] === "corrected" ? (
                  <textarea
                    className="w-full h-16 border border-primary/50 rounded p-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                    value={corrections[key] ?? ""}
                    onChange={(e) =>
                      setCorrections((c) => ({ ...c, [key]: e.target.value }))
                    }
                  />
                ) : (
                  <pre className="text-xs font-mono text-slate-600 bg-slate-50 rounded p-2 max-h-16 overflow-auto whitespace-pre-wrap">
                    {typeof value === "string"
                      ? value
                      : JSON.stringify(value, null, 0)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || fields.length === 0}
          >
            {submitting
              ? "Enregistrement..."
              : correctedCount > 0
                ? `Valider (${correctedCount} correction${correctedCount > 1 ? "s" : ""})`
                : "Valider comme correct"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
